import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

import { logApiNetworkError } from '@apps/web/lib/datadog_logs';
import { EVENTS } from '@apps/web/lib/events';
import { isExtensionContext } from '@apps/web/lib/extension_context';
import { getExtensionSessionToken } from '@apps/web/lib/extension_session_token';
import { modeStore } from '@apps/web/lib/mode_store';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import {
    type ApiClient,
    type ApiClientNetworkErrorContext,
    createApiClient,
    type FetchResponse,
    isAbortError,
    shouldRetryApiClientNetworkError,
} from '@libraries/typescript/api_client';

let client: ApiClient | null = null;
const PHOEBE_CLIENT_HEADER_NAME = 'X-Phoebe-Client';
const PHOEBE_CLIENT_HEADER_VALUE = 'chrome-extension';
const BROWSER_FETCH_TRANSPORT_MESSAGES = new Set([
    'Failed to fetch',
    'Load failed',
    'NetworkError when attempting to fetch resource.',
]);

// The Sentry SDK's own fetch instrumentation wraps `window.fetch` outside this
// module and rewrites a transport rejection's message to `<message> (<host>)`
// before rethrowing, so the message reaching us never matches the list above
// verbatim. Strip that suffix before comparing.
const SENTRY_FETCH_HOST_SUFFIX_PATTERN = / \([^()]*\)$/;

function isBrowserFetchTransportError(error: unknown): boolean {
    return (
        error instanceof TypeError &&
        BROWSER_FETCH_TRANSPORT_MESSAGES.has(
            error.message.replace(SENTRY_FETCH_HOST_SUFFIX_PATTERN, ''),
        )
    );
}

function captureSettingsChange(
    url: string,
    init: Parameters<typeof fetch>[1] | undefined,
    response: FetchResponse,
): void {
    if (!response.ok || typeof window === 'undefined') {
        return;
    }
    const method = init?.method?.toUpperCase() ?? 'GET';
    if (method === 'GET') {
        return;
    }
    const currentPath = window.location.pathname;
    if (!currentPath.startsWith('/settings')) {
        return;
    }
    let apiPath: string;
    try {
        apiPath = new URL(url, window.location.origin).pathname;
    } catch {
        apiPath = url;
    }
    try {
        posthog.capture(EVENTS.SETTINGS_CHANGED, {
            api_path: apiPath,
            method,
            organization_id:
                apiPath.match(/\/organizations\/([^/]+)/)?.[1] ?? null,
            settings_pathname: currentPath,
            settings_surface: 'redesign',
        });
    } catch {
        // Analytics must never interfere with a successful settings save.
    }
}

function reportApiNetworkError(context: ApiClientNetworkErrorContext): void {
    const errorMessage =
        context.error instanceof Error
            ? context.error.message
            : 'Network request failed';
    const errorName =
        context.error instanceof Error ? context.error.name : null;
    const willRetry = shouldRetryApiClientNetworkError(context);
    const browserContext = {
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        visibilityState:
            typeof document !== 'undefined' ? document.visibilityState : null,
        pageOrigin:
            typeof window !== 'undefined' ? window.location.origin : null,
        pagePath:
            typeof window !== 'undefined' ? window.location.pathname : null,
    };

    // A caller cancellation is not a failure: long-lived readers (the agent-run
    // event stream, SWR revalidations) abort their in-flight request on every
    // unmount, navigation, and dependency change, and their own handlers
    // already treat that as routine. Reporting it drowned the real signal —
    // Sentry WEB-93 alone logged 5,743 error-level events across 416 users.
    // Keep a breadcrumb so a cancellation still shows up as context underneath
    // a genuine error that follows it.
    if (isAbortError(context.error)) {
        Sentry.addBreadcrumb({
            category: 'api.fetch',
            level: 'info',
            message: 'API request cancelled by caller',
            data: {
                ...browserContext,
                apiUrl: context.apiUrl,
                method: context.method,
                pathTemplate: context.pathTemplate,
                requestId: context.requestId,
                attempt: context.attempt,
                maxAttempts: context.maxAttempts,
                errorMessage,
                errorName,
                mode: context.mode,
                reason: context.reason,
            },
        });
        return;
    }

    if (!willRetry) {
        logApiNetworkError({
            method: context.method,
            pathTemplate: context.pathTemplate,
            attempt: context.attempt,
            maxAttempts: context.maxAttempts,
            mode: context.mode,
            reason: context.reason,
            online: browserContext.online,
            visibilityState: browserContext.visibilityState,
            errorName,
        });
    }

    if (willRetry) {
        Sentry.addBreadcrumb({
            category: 'api.fetch',
            level: 'warning',
            message: 'Transient API network failure, retrying GET request',
            data: {
                ...browserContext,
                apiUrl: context.apiUrl,
                method: context.method,
                pathTemplate: context.pathTemplate,
                requestId: context.requestId,
                attempt: context.attempt,
                maxAttempts: context.maxAttempts,
                errorMessage,
                errorName,
                mode: context.mode,
                reason: context.reason,
            },
        });
        return;
    }

    if (
        browserContext.online === false ||
        browserContext.visibilityState === 'hidden'
    ) {
        Sentry.addBreadcrumb({
            category: 'api.fetch',
            level: 'warning',
            message: 'Background API network failure suppressed',
            data: {
                ...browserContext,
                apiUrl: context.apiUrl,
                method: context.method,
                pathTemplate: context.pathTemplate,
                requestId: context.requestId,
                attempt: context.attempt,
                maxAttempts: context.maxAttempts,
                errorMessage,
                errorName,
                mode: context.mode,
                reason: context.reason,
            },
        });
        return;
    }

    if (context.bestEffort === true) {
        Sentry.addBreadcrumb({
            category: 'api.fetch',
            level: 'info',
            message: 'Best-effort API network failure suppressed',
            data: {
                ...browserContext,
                apiUrl: context.apiUrl,
                method: context.method,
                pathTemplate: context.pathTemplate,
                requestId: context.requestId,
                attempt: context.attempt,
                maxAttempts: context.maxAttempts,
                errorMessage,
                errorName,
                mode: context.mode,
                reason: context.reason,
            },
        });
        return;
    }

    // A terminal browser transport failure (dropped connection, captive
    // portal, VPN flap) is a client-side condition Phoebe cannot act on, so it
    // is reported at warning and grouped into one issue of its own instead of
    // joining the endpoint's error issue. It stays in Sentry rather than being
    // dropped because a burst across many users is one of the few client-side
    // signals that the API itself became unreachable.
    const isTransportFailure = isBrowserFetchTransportError(context.error);

    Sentry.withScope((scope) => {
        scope.setLevel(isTransportFailure ? 'warning' : 'error');
        scope.setFingerprint(
            isTransportFailure
                ? ['api-browser-transport-failure']
                : ['api-network-error', context.method, context.pathTemplate],
        );
        scope.setTag('api_method', context.method);
        scope.setTag('api_path_template', context.pathTemplate);
        scope.setTag('api_retry_attempt', String(context.attempt));
        if (context.reason) {
            scope.setTag('api_reason', context.reason);
        }
        scope.setContext('api_request', {
            apiUrl: context.apiUrl,
            method: context.method,
            pathTemplate: context.pathTemplate,
            requestId: context.requestId,
            attempt: context.attempt,
            maxAttempts: context.maxAttempts,
            errorMessage,
            errorName,
            mode: context.mode ?? null,
            reason: context.reason ?? null,
        });
        scope.setContext('browser', browserContext);
        Sentry.captureException(
            context.error instanceof Error
                ? context.error
                : new Error(errorMessage),
        );
    });
}

// The extension iframe is mounted at `/extension` (or rendered inside
// the local preview at `/extension-local`). It always operates on real
// EHR data, so sandbox mode is meaningless there — a user who left the
// main web app in sandbox mode would otherwise see every selected EHR
// shift resolve as "not synced" and have no way to recover without
// leaving the extension. Force live mode for iframe requests, and do
// NOT write back to the shared `modeStore` so the iframe's forced
// mode cannot clobber the main web app's sandbox state.
//
// Resolve the context per request (not at singleton construction) so
// the same `getApiClient()` instance behaves correctly across SPA
// route changes — e.g. a first call on `/shifts` that constructs the
// client must still honor forced live mode when the same tab later
// navigates into `/extension-local`, and vice versa.
const isExtensionIframeContext = isExtensionContext;

function resolveRequestMode(
    requestedMode?: 'live' | 'sandbox',
): 'live' | 'sandbox' | undefined {
    if (isExtensionIframeContext()) {
        return 'live';
    }
    return requestedMode ?? modeStore.getMode();
}

export async function showTransparentRateLimitNotice(
    signal?: AbortSignal,
): Promise<void> {
    if (signal?.aborted) {
        return;
    }
    // The notification module statically imports this configured client, so
    // loading it only when needed avoids a module cycle.
    const { toast } = await import('@apps/web/lib/app_notifications');
    if (signal?.aborted) {
        return;
    }
    toast.error('That action didn’t go through. Please try again.', {
        persistNotification: false,
    });
}

export function getApiClient(): ApiClient {
    if (!client) {
        const runtimeConfig = getRuntimeConfig();
        client = createApiClient({
            baseUrl: runtimeConfig.apiUrl,
            clientVersion: runtimeConfig.sha,
            fetch: (url, init) => {
                const extensionSessionToken = getExtensionSessionToken();
                const headers = new Headers(init?.headers);

                if (extensionSessionToken && !headers.has('Authorization')) {
                    headers.set(
                        'Authorization',
                        `Bearer ${extensionSessionToken}`,
                    );
                }
                if (
                    isExtensionIframeContext() &&
                    !headers.has(PHOEBE_CLIENT_HEADER_NAME)
                ) {
                    headers.set(
                        PHOEBE_CLIENT_HEADER_NAME,
                        PHOEBE_CLIENT_HEADER_VALUE,
                    );
                }

                return fetch(url, {
                    ...init,
                    credentials: extensionSessionToken ? 'omit' : 'include',
                    headers,
                }).then((response) => {
                    captureSettingsChange(url, init, response);
                    return response;
                });
            },
            getMode: resolveRequestMode,
            onNetworkError: reportApiNetworkError,
            onTransparentRateLimitExhausted: ({ signal }) =>
                showTransparentRateLimitNotice(signal),
        });
    }
    return client;
}
